import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { MeResponse } from '../api/auth'
import { isStaffNext, postLoginRoute } from '../auth/postLoginRoute'
import { ManagerLoginForm } from './login/ManagerLoginForm'
import { PlayerLogin } from './login/PlayerLogin'

/**
 * `/login` — dos entradas, una por tipo de usuario:
 *
 *   - por DEFECTO, el JUGADOR: código de 6 dígitos en un pad (ver
 *     `login/PlayerLogin.tsx`), con el paso previo del código del kit si el
 *     dispositivo todavía no está ligado a ningún club.
 *   - "Soy manager": el formulario clásico email/password + Google
 *     (`login/ManagerLoginForm.tsx`), único camino válido para `/admin` y
 *     para los paneles de club. Desde ahí se vuelve con "Soy jugador".
 *
 * Arranca en el formulario de manager cuando el contexto lo delata: un
 * `?next=` a `/admin` o `/club` (ahí rebotan `RequireAdminShell` y el
 * `RequireAuth` de `/club`) o un `?email=` de link de invitación.
 *
 * El destino después de entrar lo decide `postLoginRoute` — la misma cadena
 * de gates que usa el botón Train de `AppNavBar`.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const next = params.get('next')
  const presetEmail = (params.get('email') || '').trim().toLowerCase()
  // Contexto de la invitación (lo cuelga `InvitePage` al rebotar acá): qué
  // cuestionario le piden y de parte de qué club. Sólo copy — el destino
  // real sigue viajando en `next`.
  const invite = presetEmail
    ? {
        code: (params.get('code') || '').trim(),
        name: (params.get('qname') || '').trim(),
        club: (params.get('club') || '').trim(),
      }
    : null

  const [mode, setMode] = useState<'player' | 'manager'>(
    isStaffNext(next) || presetEmail ? 'manager' : 'player',
  )

  function onAuthenticated(user: MeResponse) {
    navigate(postLoginRoute(user, next), { replace: true })
  }

  if (mode === 'manager') {
    return (
      <ManagerLoginForm
        next={next}
        presetEmail={presetEmail}
        invite={invite}
        initialError={params.get('error')}
        onAuthenticated={onAuthenticated}
        onPlayer={() => setMode('player')}
      />
    )
  }

  return (
    <PlayerLogin onAuthenticated={onAuthenticated} onManager={() => setMode('manager')} />
  )
}
